/**
 * Single source of truth for pricing, AI cost markups, and tier limits.
 *
 * Revenue model:
 *   • Base = per-seat/mo subscription
 *   • AI usage = pay-as-you-go with TIERED markups:
 *       Free       → 100× internal cost (99% margin — premium for uncommitted users)
 *       Pro        →  10× internal cost (90% margin — volume incentive to upgrade)
 *       Enterprise →   7× internal cost (85% margin — best rate, rewards commitment)
 *   • Enterprise is UNCAPPED — unlimited overage billed at 7×
 *   • Premium AI tiers for high-value operations
 *
 * Revenue protection guarantees:
 *   1. Free tier: hard stop at limits — $0 cost exposure beyond ~$1.55/user/mo
 *   2. Pro tier: 5× overage cap then contact sales — limits exposure
 *   3. Enterprise tier: uncapped but ALWAYS billed at 7× cost (85% margin floor)
 *   4. Payment failure → immediate AI access block (check_and_increment_usage)
 *   5. Overage counters persist across period rollover until billed by report-overage
 *   6. Subscription cancellation triggers final overage billing before downgrade
 *   7. Platform minimum: $0 infrastructure cost per free user beyond AI calls
 *
 * Value-based justification (not just cost-plus):
 *   Each AI evaluation saves ~12 min of manual CRM review ($4–$8 labor cost).
 *   At $2.80/eval (Free), we're still 30–50% below the labor alternative.
 *   At $0.20/eval (Enterprise), it's a 95%+ savings vs manual work.
 *
 * Enterprise stickiness mechanisms:
 *   - Annual commitment required (20% discount incentive)
 *   - Minimum 12-month contract for Enterprise
 *   - Data gravity: historical patterns, mappings, corrections compound value
 *   - Custom integrations + SSO + audit log create switching costs
 *   - Brain Improvement Credits reward data contribution
 *   - Dedicated CSM relationship
 *
 * Investment thesis ($5-10M):
 *   - 85%+ gross margins on ALL tiers at scale
 *   - Net revenue retention >120% from usage growth + tier upgrades
 *   - $0 marginal cost for platform features (only AI calls cost)
 *   - Enterprise ARPU: $249/seat × avg 15 seats = $44,820/yr base + overage
 *   - Path to $3M+ ARR with 70 enterprise accounts
 *   - Autonomous Brain = compounding moat (accuracy improves with usage)
 */

export type UsageTier = 'free' | 'pro' | 'enterprise';
export type AIServiceLevel = 'standard' | 'premium';

// ── Internal AI costs (our cost per unit) ──────────────────────────
export const AI_COSTS = {
  evaluation:    0.028,
  transcription: 0.035,
  repair:        0.042,
  ai_mapping:    0.015,
} as const;

// ── Tiered markup multipliers ──────────────────────────────────────
// Free users pay premium (100×) — taste, not a deal
// Pro users get strong value (10×) — incentivizes upgrade
// Enterprise gets best rate (7×) — rewards commitment at scale
export const TIER_MARKUPS: Record<UsageTier, number> = {
  free:       100,
  pro:        10,
  enterprise: 7,
} as const;

// ── Premium AI service multiplier ──────────────────────────────────
// Standard AI = base markup. Premium AI = 2× the tier markup.
// Premium: higher-accuracy models, priority queue, SLA-backed.
export const SERVICE_LEVEL_MULTIPLIER: Record<AIServiceLevel, number> = {
  standard: 1,
  premium:  2,   // 2× the already-tiered rate
} as const;

// ── Customer-facing per-unit prices (by tier × service level) ──────
export function getAIPrice(
  operation: keyof typeof AI_COSTS,
  tier: UsageTier,
  serviceLevel: AIServiceLevel = 'standard',
): number {
  const cost = AI_COSTS[operation];
  const markup = TIER_MARKUPS[tier];
  const slMultiplier = SERVICE_LEVEL_MULTIPLIER[serviceLevel];
  return +(cost * markup * slMultiplier).toFixed(2);
}

// ── Quick-access price tables (standard service level) ─────────────
export const AI_PRICES = {
  free: {
    evaluation:    getAIPrice('evaluation', 'free'),       // $2.80
    transcription: getAIPrice('transcription', 'free'),    // $3.50
    repair:        getAIPrice('repair', 'free'),           // $4.20
    ai_mapping:    getAIPrice('ai_mapping', 'free'),       // $1.50
  },
  pro: {
    evaluation:    getAIPrice('evaluation', 'pro'),        // $0.28
    transcription: getAIPrice('transcription', 'pro'),     // $0.35
    repair:        getAIPrice('repair', 'pro'),            // $0.42
    ai_mapping:    getAIPrice('ai_mapping', 'pro'),        // $0.15
  },
  enterprise: {
    evaluation:    getAIPrice('evaluation', 'enterprise'),  // $0.20
    transcription: getAIPrice('transcription', 'enterprise'),// $0.25
    repair:        getAIPrice('repair', 'enterprise'),      // $0.29
    ai_mapping:    getAIPrice('ai_mapping', 'enterprise'),  // $0.11
  },
} as const;

// ── Premium AI price tables (2× standard) ──────────────────────────
export const AI_PRICES_PREMIUM = {
  free: {
    evaluation:    getAIPrice('evaluation', 'free', 'premium'),       // $5.60
    transcription: getAIPrice('transcription', 'free', 'premium'),    // $7.00
    repair:        getAIPrice('repair', 'free', 'premium'),           // $8.40
    ai_mapping:    getAIPrice('ai_mapping', 'free', 'premium'),       // $3.00
  },
  pro: {
    evaluation:    getAIPrice('evaluation', 'pro', 'premium'),        // $0.56
    transcription: getAIPrice('transcription', 'pro', 'premium'),     // $0.70
    repair:        getAIPrice('repair', 'pro', 'premium'),            // $0.84
    ai_mapping:    getAIPrice('ai_mapping', 'pro', 'premium'),        // $0.30
  },
  enterprise: {
    evaluation:    getAIPrice('evaluation', 'enterprise', 'premium'),  // $0.39
    transcription: getAIPrice('transcription', 'enterprise', 'premium'),// $0.49
    repair:        getAIPrice('repair', 'enterprise', 'premium'),      // $0.59
    ai_mapping:    getAIPrice('ai_mapping', 'enterprise', 'premium'),  // $0.21
  },
} as const;

// ── Seat pricing ──────────────────────────────────────────────────
export const SEAT_PRICING = {
  free:       { monthly: 0,   annual: 0   },
  pro:        { monthly: 89,  annual: 79  },   // $89/seat/mo, $79 annual
  enterprise: { monthly: 199, annual: 149 },   // $199/seat/mo, $149 annual
} as const;

// ── Included AI credits per seat/mo (taste for free, base for paid) ─
export const TIER_LIMITS = {
  free:       { eval: 25,   trans: 10,  repair: 10,  mapping: 5   },
  pro:        { eval: 500,  trans: 100, repair: 100, mapping: 50  },
  enterprise: { eval: 2000, trans: 500, repair: 500, mapping: 200 },
} as const;

// ── Overage caps ──────────────────────────────────────────────────
// Free = hard stop, Pro = 5× then contact sales, Enterprise = UNCAPPED
export const OVERAGE_CAPS = {
  free:       0,          // hard stop — upgrade to continue
  pro:        5,          // up to 5× base then contact sales
  enterprise: Infinity,   // uncapped — billed at 7× cost, no ceiling
} as const;

// ── Enterprise commitment requirements ────────────────────────────
export const ENTERPRISE_TERMS = {
  minimum_annual_contract: true,
  minimum_seats: 5,
  minimum_annual_value: 11_940, // 5 seats × $199/mo × 12 = $11,940/yr minimum (or $149 annual = $8,940)
  early_termination_fee_pct: 50, // 50% of remaining contract value
  overage_premium_pct: 0, // no premium on standard overage — already at 7× markup
} as const;

// ── Revenue protection: max cost exposure per tier ────────────────
export const MAX_COST_EXPOSURE = {
  free: {
    per_user_per_month: +(
      AI_COSTS.evaluation * TIER_LIMITS.free.eval +
      AI_COSTS.transcription * TIER_LIMITS.free.trans +
      AI_COSTS.repair * TIER_LIMITS.free.repair +
      AI_COSTS.ai_mapping * TIER_LIMITS.free.mapping
    ).toFixed(2), // ~$1.55 — hard stop after this
    revenue: 0,
    net: -1.55, // acceptable loss-leader
  },
  pro: {
    per_user_per_month_included: +(
      AI_COSTS.evaluation * TIER_LIMITS.pro.eval +
      AI_COSTS.transcription * TIER_LIMITS.pro.trans +
      AI_COSTS.repair * TIER_LIMITS.pro.repair +
      AI_COSTS.ai_mapping * TIER_LIMITS.pro.mapping
    ).toFixed(2), // ~$18.85
    revenue_monthly: SEAT_PRICING.pro.monthly, // $89
    margin_on_included: +(89 - 18.85).toFixed(2), // $70.15 (78.8% margin)
    overage_margin_pct: 90, // 10× markup on overage
  },
  enterprise: {
    per_user_per_month_included: +(
      AI_COSTS.evaluation * TIER_LIMITS.enterprise.eval +
      AI_COSTS.transcription * TIER_LIMITS.enterprise.trans +
      AI_COSTS.repair * TIER_LIMITS.enterprise.repair +
      AI_COSTS.ai_mapping * TIER_LIMITS.enterprise.mapping
    ).toFixed(2), // ~$76.60
    revenue_monthly: SEAT_PRICING.enterprise.monthly, // $199
    margin_on_included: +(199 - 76.60).toFixed(2), // $122.40 (61.5% margin)
    overage_margin_pct: 85.7, // 7× markup on overage
  },
} as const;

// ── Value justification data (for UI and sales) ───────────────────
export const VALUE_JUSTIFICATION = {
  evaluation: {
    manual_time_minutes: 12,
    manual_labor_cost: 6.00,   // avg cost of 12 min at $30/hr
    description: 'Replaces 12 min of manual CRM field review per record',
  },
  transcription: {
    manual_time_minutes: 20,
    manual_labor_cost: 10.00,
    description: 'Replaces 20 min of manual note-taking and data entry',
  },
  repair: {
    manual_time_minutes: 15,
    manual_labor_cost: 7.50,
    description: 'Auto-corrects CRM fields that would take 15 min to verify and fix',
  },
  ai_mapping: {
    manual_time_minutes: 30,
    manual_labor_cost: 15.00,
    description: 'Replaces 30 min of manual schema mapping per field set',
  },
} as const;

// ── Tier feature differentiation (beyond credits) ─────────────────
export const TIER_FEATURES = {
  free: {
    integrations: ['HubSpot'],
    analytics: 'basic',
    support: 'community',
    retention_days: 30,
    export_formats: ['csv'],
    ai_service_level: 'standard' as AIServiceLevel,
    sla: null,
    sso: false,
    audit_log: false,
    custom_rules: 5,
    webhooks: 0,
    api_access: false,
  },
  pro: {
    integrations: ['HubSpot', 'Salesforce', 'Close'],
    analytics: 'advanced',
    support: 'priority_email',
    retention_days: 90,
    export_formats: ['csv', 'json', 'pdf'],
    ai_service_level: 'standard' as AIServiceLevel,
    sla: null,
    sso: false,
    audit_log: true,
    custom_rules: 50,
    webhooks: 5,
    api_access: false,
  },
  enterprise: {
    integrations: ['HubSpot', 'Salesforce', 'Close', 'Attio', 'Custom'],
    analytics: 'full_suite',
    support: 'dedicated_csm',
    retention_days: 365,
    export_formats: ['csv', 'json', 'pdf', 'xlsx', 'api'],
    ai_service_level: 'premium' as AIServiceLevel,
    sla: '99.9% uptime',
    sso: true,
    audit_log: true,
    custom_rules: Infinity,
    webhooks: Infinity,
    api_access: true,
  },
} as const;

// ── Margin calculation helpers ────────────────────────────────────
export function aiMarginPct(tier: UsageTier): number {
  const markup = TIER_MARKUPS[tier];
  return ((markup - 1) / markup) * 100;
}

/** Savings vs manual labor for a given operation */
export function savingsVsManual(
  operation: keyof typeof AI_COSTS,
  tier: UsageTier,
): { pricePer: number; laborCost: number; savingsPct: number } {
  const pricePer = getAIPrice(operation, tier);
  const laborCost = VALUE_JUSTIFICATION[operation].manual_labor_cost;
  const savingsPct = ((laborCost - pricePer) / laborCost) * 100;
  return { pricePer, laborCost, savingsPct: Math.max(0, savingsPct) };
}
